import { FlexBox, Icon, IconButton, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { Link } from 'react-router-dom';

import { WebsocketRequestMessage } from 'app/middleware/websocket/types/outboundWebsocketEvent';
import { RootReducerStateT } from 'app/reducers/rootReducer';
import { createSearchUrl } from 'app/views/layout/views/search/utils';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppUrl } from 'utils/url/createAppUrl';

import { TypingType } from '../../conversation/reducers/typing';
import { PresenceList } from '../../presence/components/presenceList';

import { CopilotTrialButton } from './components/copilotTrialButton';
import { FooterItem } from './components/footerItem';
import { PageNavItem } from './components/pageNavItem';
import { Conversations } from './conversations';
import { MenuHeader } from './menuHeader';
import styles from './sidebarMenu.module.scss';

export type DevNoticeT = {
  title: string;
  label: string;
  onClose?: VoidFunction;
};

export type StatePropsT = {
  search: RootReducerStateT['search'];
  metaConversation: RootReducerStateT['conversation']['metaConversation'];
  metaConversations: RootReducerStateT['conversations']['metaConversations'];
  metaConversationsBeforeBreak: RootReducerStateT['conversations']['metaConversations'];
  metaConversationsAfterBreak: RootReducerStateT['conversations']['metaConversations'];
  typings: RootReducerStateT['typing']['typings'];
  conversationsLastSearchParams: RootReducerStateT['conversationsPage']['lastSearchParams'];
  isMenuOpen: RootReducerStateT['settings']['personal']['isLeftMenuOpen'];
  menuDevNotices: DevNoticeT[];
  showWidget: boolean;
};

export type LeftMenuTogglePayloadT = { key: string; value: boolean };

export type DispatchPropsT = {
  showUserModal: () => void;
  typingRemove: (typing: TypingType) => void;
  conversationsRemove: (csid: number) => void;
  wsRequest: (payload: WebsocketRequestMessage) => Promise<unknown>;
  personalLeftMenuToggle: (payload: LeftMenuTogglePayloadT) => Promise<unknown>;
};

export type OwnPropsT = {
  isMenuAlwaysOpen?: boolean;
  onToggleNavigation?: () => void;
};

export type SidebarMenuProps = StatePropsT & DispatchPropsT & OwnPropsT;

export const SidebarMenu = (props: SidebarMenuProps): JSX.Element => {
  const featureFlags = useFeatureFlag([
    'has-chat-with-dixa-support',
    'knowledge-access',
    'custom-analytics',
    'new-search-conversations-view',
  ]);

  const hasChatWithDixaSupport =
    featureFlags.hasChatWithDixaSupport && props.showWidget;
  const hasKnowledgeEnabled = featureFlags.knowledgeAccess;
  const hasAnalytics = featureFlags.customAnalytics;

  const toggleMenu = () => {
    props.personalLeftMenuToggle({
      key: 'isLeftMenuOpen',
      value: !props.isMenuOpen,
    });

    props.isMenuOpen ? ampli.sidebarCollapsed() : ampli.sidebarExpanded();
  };

  const getSearchLink = () => {
    return createSearchUrl(props.search.params || {});
  };

  const openDixaMessenger = (): void => {
    if (hasChatWithDixaSupport) {
      window._dixa_?.invoke('setWidgetOpen', true);
    }
  };

  const handleAddAgentButtonClick = (): void => {
    ampli.sidebarAddAgentButtonClicked();
    props.showUserModal();
  };

  const {
    isMenuOpen,
    isMenuAlwaysOpen,
    menuDevNotices,
    conversationsLastSearchParams,
  } = props;

  const hasInviteAccess = useHasAccess('organization.invite');

  const hasKnowledgeReadAccess = useHasAccess('knowledge.content:r');

  const hasKnowledgeWriteAccess = useHasAccess('knowledge.content:w');

  const shouldShowFooter =
    hasChatWithDixaSupport || isMenuAlwaysOpen || !isMenuOpen;

  const rootClass = classnames({
    [styles.root]: true,
    [styles.rootCollapsed]: !isMenuAlwaysOpen && !isMenuOpen,
  });

  const conversationsPath = createAppUrl({
    path: '/conversations',
    params: { search: conversationsLastSearchParams },
  });

  const hasConversations =
    props.metaConversationsBeforeBreak.length > 0 ||
    props.metaConversationsAfterBreak.length > 0;

  return (
    <div className={rootClass}>
      <nav className={styles.sidebar} role="navigation" id="main-sidebar">
        <ul className={styles.headWrapper}>
          <li>
            {!isMenuAlwaysOpen ? (
              <a onClick={toggleMenu} className={styles.toggleMenuContainer}>
                <Icon
                  size={24}
                  name={isMenuOpen ? 'angle-double-left' : 'angle-double-right'}
                  className={styles.toggleMenu}
                />
              </a>
            ) : (
              <a
                onClick={props.onToggleNavigation}
                title="Toggle menu"
                className={styles.toggleMenuContainer}
                style={{ opacity: '1' }}
              >
                <Icon size={24} name="close" className={styles.toggleMenu} />
              </a>
            )}
          </li>
          <li>
            <Link
              to="/"
              title="Dashboard"
              className={styles.logoContainer}
              onClick={() => {
                props.onToggleNavigation?.();
                ampli.sidebarNavClicked({ item: 'Logo' });
              }}
            >
              <Icon size={20} name="dixa" className={styles.logo} />
            </Link>
          </li>
        </ul>

        {menuDevNotices.map((notice: DevNoticeT, index: number) => (
          <Tooltip key={`menu-notice-${index}`} content={notice.label}>
            <div className={styles.devNotice} title={notice.title}>
              {notice.label}

              {notice.onClose && (
                <IconButton
                  icon="close"
                  variant="danger"
                  className={styles.devNoticeClose}
                  onClick={notice.onClose}
                />
              )}
            </div>
          </Tooltip>
        ))}

        <ul className={styles.pageNav}>
          <CopilotTrialButton onToggleNavigation={props.onToggleNavigation} />

          <PageNavItem
            to="/dashboard"
            title="Dashboard"
            icon="dashboard"
            onClick={props.onToggleNavigation}
          />
          <PageNavItem
            to={
              featureFlags.newSearchConversationsView
                ? getSearchLink()
                : conversationsPath
            }
            title="Conversations"
            icon="conversations"
            onClick={props.onToggleNavigation}
          />
          <PageNavItem
            to={'/contacts'}
            title="Contacts"
            icon="id-card"
            onClick={props.onToggleNavigation}
          />
          {!featureFlags.newSearchConversationsView && (
            <PageNavItem
              to={getSearchLink()}
              title="Search"
              icon="search"
              onClick={props.onToggleNavigation}
            />
          )}

          <PageNavItem
            to="/team"
            title="Team overview"
            icon="team"
            onClick={props.onToggleNavigation}
          />

          {hasAnalytics && (
            <PageNavItem
              to="/analytics"
              title="Intelligence"
              icon="chart-bar"
              onClick={props.onToggleNavigation}
            />
          )}

          {hasKnowledgeWriteAccess && hasKnowledgeEnabled && (
            <PageNavItem
              to="/knowledge"
              title="Knowledge"
              icon="file-text"
              onClick={props.onToggleNavigation}
            />
          )}
          {hasKnowledgeReadAccess && hasKnowledgeEnabled && (
            <PageNavItem
              to="/internal-kb"
              title="Internal Knowledge Base"
              icon="file-text-search"
              onClick={props.onToggleNavigation}
            />
          )}
          <PageNavItem
            to="/settings"
            title="Settings"
            icon="cog"
            onClick={props.onToggleNavigation}
          />
        </ul>

        {hasConversations && (
          <div>
            <MenuHeader
              title="Conversations"
              isMenuOpen={isMenuAlwaysOpen || isMenuOpen}
            />

            <Conversations
              typings={props.typings}
              conversationsRemove={props.conversationsRemove}
              typingRemove={props.typingRemove}
              wsRequest={props.wsRequest}
              isMenuAlwaysOpen={props.isMenuAlwaysOpen}
              conversationsLastSearchParams={
                props.conversationsLastSearchParams
              }
              isMenuOpen={props.isMenuOpen}
              onToggleNavigation={props.onToggleNavigation}
              metaConversationsBeforeBreak={props.metaConversationsBeforeBreak}
              metaConversationsAfterBreak={props.metaConversationsAfterBreak}
            />
          </div>
        )}

        {(isMenuAlwaysOpen || isMenuOpen) && (
          <div className={styles.presenceList}>
            <FlexBox
              alignItems="center"
              justifyContent="flex-end"
              height="24px"
            >
              {hasInviteAccess && (
                <Tooltip content="Add new agent">
                  <a
                    className={styles.newUserButton}
                    onClick={handleAddAgentButtonClick}
                    role="button"
                  >
                    <Icon name="user-plus" size={16} />
                  </a>
                </Tooltip>
              )}
            </FlexBox>

            <PresenceList />
          </div>
        )}

        {shouldShowFooter && (
          <ul className={styles.sidebarFooter}>
            {!isMenuOpen && !isMenuAlwaysOpen && (
              <FooterItem icon="users" onClick={toggleMenu} />
            )}

            {hasChatWithDixaSupport && (
              <FooterItem icon="chat" onClick={openDixaMessenger}>
                Chat with support
              </FooterItem>
            )}
          </ul>
        )}
      </nav>
    </div>
  );
};
